import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Badge, Button, Field, Icon, Modal } from '@evogard/ui';
import {
  businessDate,
  closesAutomatically,
  competenceClosingDate,
  formatDate,
} from '@evogard/domain';
import type { Period, StatementClosing } from '@evogard/contract';
import { api, ok } from '../lib/api.js';

/**
 * O FECHAMENTO da competência do extrato — o selo e o modal que fecha.
 *
 * ## Por que mora aqui, e não em `modules/sales`
 *
 * Até 25/09/2026 os botões de fechar, recalcular e reabrir moravam no topo do
 * Extrato, e o dono os tirou de lá: o Extrato é o documento de UMA pessoa, e o
 * fechamento é um ato da praça inteira. O ato foi para Comercial › Pagamentos Recorrência,
 * ao lado da Janela de envio — é lá que quem paga decide que o mês acabou. O
 * selo Aberta/Fechada continua no Extrato, só em leitura, porque ele muda o que
 * o total significa.
 *
 * Duas telas de módulos diferentes usam estas peças, e uma tela de módulo não
 * importa da pasta de outro — o mesmo motivo de `PaymentParts.tsx`.
 *
 * ## A permissão continua a mesma
 *
 * `sales.statement:fechar` cobre os três atos, e as rotas não mudaram. Mudou só
 * onde o botão está.
 */

/** `2026-09` → "setembro de 2026". UTC pelo motivo de `periodLabel` do comercial. */
function periodName(period: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${period}-01T00:00:00Z`));
}

/** A competência de hoje, no fuso da casa — `businessDate`, como no comercial. */
function thisPeriod(): Period {
  return businessDate(new Date()).slice(0, 7) as Period;
}

/**
 * O estado da competência em uma frase, com o selo.
 *
 * É o que o Extrato mostra no topo, e o que o modal mostra acima dos botões:
 * as duas leituras saem daqui para não dizerem coisas diferentes do mesmo mês.
 */
export function ClosingState({
  closing,
  month,
}: {
  closing: StatementClosing | null;
  month: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <Badge tone={closing === null ? 'neutro' : 'sucesso'}>
          {closing === null ? 'Aberta' : 'Fechada'}
        </Badge>
        <p className="text-sm text-texto-suave">
          {closing === null ? (
            <>
              {periodName(month)} ainda está aberta: a esteira continua trazendo boleto, e
              o total pode subir.
              {/*
               * A data em que ela fecha sozinha (19/10/2026). Só de outubro de
               * 2026 em diante: a anterior a isso fica com quem a fecha à mão,
               * e dizer uma data passada seria prometer o que não acontece.
               */}
              {closesAutomatically(month) &&
                ` Fecha automaticamente em ${formatDate(competenceClosingDate(month))}, se ninguém fechar antes.`}
            </>
          ) : (
            <>
              {periodName(month)} foi fechada{' '}
              {closing.automatic ? 'automaticamente ' : ''}em{' '}
              {formatDate(closing.closedAt.slice(0, 10))}
              {closing.closedBy === null ? '' : ` por ${closing.closedBy}`}. O extrato
              deste mês não muda mais.
            </>
          )}
        </p>
      </div>

      {/*
       * O aviso do que chegou DEPOIS do fechamento ("O espelho tem hoje N
       * boletos pagos desta competência sem entrada gravada…") saiu do
       * comercial em 26/10/2026, por decisão do dono. A API continua
       * devolvendo `closing.afterClosure`; nenhuma tela o desenha.
       */}
    </div>
  );
}

/**
 * O modal de Pagamentos: escolher a competência e fechar, recalcular ou
 * reabrir.
 *
 * Abre no mês de HOJE, que é o que se fecha quase sempre — a competência de
 * setembro fecha em setembro. O campo de competência existe para o caso de
 * reabrir um mês passado, quando a baixa bancária de um boleto chega atrasada.
 */
export function StatementClosingModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  /*
   * O que se DIGITOU e a competência VÁLIDA são dois estados. Onde o navegador
   * desenha `type="month"` como texto (Firefox), a pessoa digita `2026-0` no
   * caminho de `2026-08`, e o campo não pode recusar a tecla.
   */
  const [typed, setTyped] = useState<string>(thisPeriod());
  const [month, setMonth] = useState<Period>(thisPeriod());
  const [reason, setReason] = useState('');
  const [reopening, setReopening] = useState(false);

  const query = useQuery({
    queryKey: ['sales', 'statement-closing', month],
    queryFn: async () =>
      ok(
        await api.sales.statementClosing({ query: { month } }),
        200,
        'Não foi possível ler o fechamento da competência.',
      ),
  });

  /*
   * Todo ato invalida TUDO: o fechamento muda o Extrato de cada consultor, a
   * Carteira e as premiações por faixa que entram no fecho — invalidar só o
   * modal deixaria as outras telas mostrando o mês como estava.
   */
  const done = () => void queryClient.invalidateQueries();

  const close = useMutation({
    mutationFn: async () =>
      ok(
        await api.sales.closeStatement({ body: { month } }),
        200,
        'Não foi possível fechar a competência.',
      ),
    onSuccess: done,
  });

  const reopen = useMutation({
    mutationFn: async () =>
      ok(
        await api.sales.reopenStatement({ body: { month, reason: reason.trim() } }),
        200,
        'Não foi possível reabrir a competência.',
      ),
    onSuccess: () => {
      setReopening(false);
      setReason('');
      done();
    },
  });

  const recalculate = useMutation({
    mutationFn: async () =>
      ok(
        await api.sales.recalculateStatement({ body: { month } }),
        200,
        'Não foi possível recalcular a competência.',
      ),
    onSuccess: done,
  });

  const busy = close.isPending || reopen.isPending || recalculate.isPending;
  const error = close.error ?? reopen.error ?? recalculate.error;
  const closing = query.data?.closing;

  /* Trocar de mês limpa o que o mês anterior deixou na tela. */
  const pick = (next: string) => {
    setTyped(next);
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(next)) return;
    setMonth(next as Period);
    setReopening(false);
    setReason('');
    close.reset();
    reopen.reset();
    recalculate.reset();
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Fechar competência"
      descricao="Fechar trava o extrato da praça inteira naquele mês e lança as premiações por faixa de placas. É um ato por base e mês, nunca por consultor. Desde outubro de 2026 o mês fecha sozinho no dia 5 do seguinte (ou no próximo dia útil bancário que não seja sexta); fechar aqui antecipa, e reabrir continua valendo."
      size="md"
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Competência"
          name="month"
          type="month"
          value={typed}
          onChange={(e) => pick(e.target.value)}
          placeholder="AAAA-MM"
          help="Abre no mês de hoje. Troque só para reabrir um mês passado."
        />

        {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

        {closing !== undefined && (
          <>
            <ClosingState closing={closing} month={month} />

            <div className="flex flex-wrap justify-end gap-2">
              {closing === null ? (
                <>
                  {/*
                   * Recalcular só existe no mês aberto. No fechado ele mudaria
                   * um número que já foi pago, e o caminho é reabrir — que
                   * pede o motivo e fica registrado.
                   */}
                  <Button
                    variant="contorno"
                    disabled={busy}
                    onClick={() => recalculate.mutate()}
                  >
                    <Icon nome="atualizar" /> Recalcular
                  </Button>
                  <Button disabled={busy} onClick={() => close.mutate()}>
                    Fechar {periodName(month)}
                  </Button>
                </>
              ) : (
                <Button
                  variant="contorno"
                  disabled={busy}
                  onClick={() => setReopening((open) => !open)}
                >
                  Reabrir
                </Button>
              )}
            </div>
          </>
        )}

        {/*
         * O motivo é campo, e não confirmação de sim/não: reabrir descongela
         * linhas que alguém já conferiu, e o motivo registra por quê — que é o
         * que alguém vai procurar seis meses depois.
         *
         * O campo na largura inteira e o botão numa fileira abaixo, como o
         * campo de competência e os botões acima. Lado a lado com
         * `items-end`, o botão alinhava com a linha de AJUDA do campo, e o
         * input ficava deslocado para cima (visto em 29/09/2026).
         */}
        {reopening && (
          <div className="flex flex-col gap-2">
            <Field
              label="Por que está reabrindo?"
              name="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              help="Fica gravado junto com o seu nome e a hora."
            />
            <div className="flex justify-end">
              <Button
                variant="perigo"
                disabled={busy || reason.trim().length < 5}
                onClick={() => reopen.mutate()}
              >
                Reabrir {periodName(month)}
              </Button>
            </div>
          </div>
        )}

        {error && <Alert tone="erro">{error.message}</Alert>}

        {/*
         * O recálculo diz as DUAS coisas: quantas entradas regravou e quais
         * premiações o espelho não respondeu. "0 entradas" sozinho era a mesma
         * frase para "não teve boleto" e para "a casa não conseguiu ler".
         */}
        {recalculate.isSuccess && (
          <Alert tone={recalculate.data.refused.length > 0 ? 'alerta' : 'sucesso'}>
            {recalculate.data.entries === 1
              ? '1 entrada regravada com o cadastro de agora.'
              : `${recalculate.data.entries} entradas regravadas com o cadastro de agora.`}
            {recalculate.data.refused.length > 0 && (
              <>
                {' '}
                {recalculate.data.refused.length === 1
                  ? 'Uma premiação não pôde ser apurada'
                  : `${recalculate.data.refused.length} premiações não puderam ser apuradas`}{' '}
                porque o espelho do ERP não respondeu, e as entradas delas ficaram como
                estavam: {recalculate.data.refused.map((r) => r.award).join(', ')}.
              </>
            )}
          </Alert>
        )}
      </div>
    </Modal>
  );
}
